import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { UserPreferences, CustomFood, RecipeIngredient, Recipe } from '../types';

interface PreferencesContextType {
  preferences: UserPreferences;
  updatePreference: <K extends keyof UserPreferences>(
    key: K,
    value: UserPreferences[K]
  ) => void;
  toggleFavorite: (foodId: string) => void;
  isFavorite: (foodId: string) => boolean;
  addCustomFood: (food: Omit<CustomFood, 'id' | 'createdAt'>) => void;
  editCustomFood: (foodId: string, updatedFood: Omit<CustomFood, 'id' | 'createdAt'>) => void;
  deleteCustomFood: (foodId: string) => void;
  getCustomFoods: () => CustomFood[];
  getCustomFood: (foodId: string) => CustomFood | undefined;
  // Recipe management
  addIngredientToRecipe: (ingredient: Omit<RecipeIngredient, 'id' | 'addedAt'>) => void;
  removeIngredientFromRecipe: (ingredientId: string) => void;
  clearCurrentRecipe: () => void;
  getCurrentRecipeIngredients: () => RecipeIngredient[];
  saveRecipe: (recipe: Omit<Recipe, 'id' | 'createdAt'>) => void;
  updateRecipe: (recipeId: string, recipe: Omit<Recipe, 'id' | 'createdAt'>) => void;
  getRecipes: () => Recipe[];
  deleteRecipe: (recipeId: string) => void;
  getCurrentLoadedRecipeId: () => string | null;
  setCurrentLoadedRecipeId: (recipeId: string | null) => void;
}

const defaultPreferences: UserPreferences = {
  carbUnit: 'gKH', // Default German unit
  language: 'de',
  darkMode: false,
  lastDbUpdate: new Date().toISOString(),
  showFPU: false,
  favorites: [],
  customFoods: [],
  recipes: [],
  currentRecipeIngredients: [],
  currentLoadedRecipeId: null
};

const PreferencesContext = createContext<PreferencesContextType | undefined>(undefined);

export const usePreferences = () => {
  const context = useContext(PreferencesContext);
  if (!context) {
    throw new Error('usePreferences must be used within PreferencesProvider');
  }
  return context;
};

interface PreferencesProviderProps {
  children: ReactNode;
}

export const PreferencesProvider: React.FC<PreferencesProviderProps> = ({ children }) => {
  const [preferences, setPreferences] = useState<UserPreferences>(() => {
    const saved = localStorage.getItem('zuckerrechner-preferences');
    if (saved) {
      const parsed = JSON.parse(saved);
      // Ensure showFPU is present for backwards compatibility
      const merged = { ...defaultPreferences, ...parsed };
      
      // Migrate old units to language-appropriate ones
      if (merged.language === 'en' && merged.carbUnit === 'gKH') {
        merged.carbUnit = 'g';
      } else if (merged.language === 'en' && (merged.carbUnit === 'BE' || merged.carbUnit === 'KE')) {
        merged.carbUnit = 'BU';
      } else if (merged.language === 'de' && merged.carbUnit === 'g') {
        merged.carbUnit = 'gKH';
      } else if (merged.language === 'de' && merged.carbUnit === 'BU') {
        merged.carbUnit = 'BE';
      }
      
      return merged;
    }
    return defaultPreferences;
  });

  useEffect(() => {
    localStorage.setItem('zuckerrechner-preferences', JSON.stringify(preferences));
    document.documentElement.classList.toggle('dark', preferences.darkMode);
  }, [preferences]);

  const updatePreference = <K extends keyof UserPreferences>(
    key: K,
    value: UserPreferences[K]
  ) => {
    setPreferences(prev => {
      const updated = { ...prev, [key]: value };
      
      // When language changes, update carbUnit to appropriate default if needed
      if (key === 'language') {
        const newLang = value as 'en' | 'de';
        const currentUnit = prev.carbUnit;
        
        // Convert units when switching languages
        if (newLang === 'en') {
          // German -> English conversions
          if (currentUnit === 'gKH') updated.carbUnit = 'g';
          else if (currentUnit === 'BE') updated.carbUnit = 'BU';
          else if (currentUnit === 'KE') updated.carbUnit = 'BU'; // KE also maps to BU
        } else {
          // English -> German conversions
          if (currentUnit === 'g') updated.carbUnit = 'gKH';
          else if (currentUnit === 'BU') updated.carbUnit = 'BE';
        }
      }
      
      return updated;
    });
  };

  const toggleFavorite = (foodId: string) => {
    setPreferences(prev => ({
      ...prev,
      favorites: prev.favorites.includes(foodId)
        ? prev.favorites.filter(id => id !== foodId)
        : [...prev.favorites, foodId]
    }));
  };

  const isFavorite = (foodId: string) => {
    return preferences.favorites.includes(foodId);
  };

  const addCustomFood = (food: Omit<CustomFood, 'id' | 'createdAt'>) => {
    const newFood: CustomFood = {
      ...food,
      id: `custom_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date().toISOString()
    };
    
    setPreferences(prev => ({
      ...prev,
      customFoods: [...prev.customFoods, newFood]
    }));
  };

  const editCustomFood = (foodId: string, updatedFood: Omit<CustomFood, 'id' | 'createdAt'>) => {
    setPreferences(prev => ({
      ...prev,
      customFoods: prev.customFoods.map(food => 
        food.id === foodId 
          ? { ...food, ...updatedFood }
          : food
      )
    }));
  };

  const deleteCustomFood = (foodId: string) => {
    setPreferences(prev => ({
      ...prev,
      customFoods: prev.customFoods.filter(food => food.id !== foodId),
      favorites: prev.favorites.filter(id => id !== foodId) // Remove from favorites too
    }));
  };

  const getCustomFoods = () => {
    return preferences.customFoods;
  };

  const getCustomFood = (foodId: string) => {
    return preferences.customFoods.find(food => food.id === foodId);
  };

  // Recipe management functions
  const addIngredientToRecipe = (ingredient: Omit<RecipeIngredient, 'id' | 'addedAt'>) => {
    const newIngredient: RecipeIngredient = {
      ...ingredient,
      id: `ingredient_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      addedAt: new Date().toISOString()
    };
    
    setPreferences(prev => ({
      ...prev,
      currentRecipeIngredients: [...prev.currentRecipeIngredients, newIngredient]
    }));
  };

  const removeIngredientFromRecipe = (ingredientId: string) => {
    setPreferences(prev => ({
      ...prev,
      currentRecipeIngredients: prev.currentRecipeIngredients.filter(ing => ing.id !== ingredientId)
    }));
  };

  const clearCurrentRecipe = () => {
    setPreferences(prev => ({
      ...prev,
      currentRecipeIngredients: []
    }));
  };

  const getCurrentRecipeIngredients = () => {
    return preferences.currentRecipeIngredients;
  };

  const saveRecipe = (recipe: Omit<Recipe, 'id' | 'createdAt'>) => {
    const newRecipe: Recipe = {
      ...recipe,
      id: `recipe_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date().toISOString()
    };
    
    setPreferences(prev => ({
      ...prev,
      recipes: [...prev.recipes, newRecipe]
      // Keep current recipe ingredients active after saving
    }));
  };

  const getRecipes = () => {
    return preferences.recipes;
  };

  const updateRecipe = (recipeId: string, recipe: Omit<Recipe, 'id' | 'createdAt'>) => {
    setPreferences(prev => ({
      ...prev,
      recipes: prev.recipes.map(existingRecipe => 
        existingRecipe.id === recipeId 
          ? { ...existingRecipe, ...recipe }
          : existingRecipe
      )
    }));
  };

  const deleteRecipe = (recipeId: string) => {
    setPreferences(prev => ({
      ...prev,
      recipes: prev.recipes.filter(recipe => recipe.id !== recipeId),
      favorites: prev.favorites.filter(id => id !== recipeId), // Remove from favorites too
      currentLoadedRecipeId: prev.currentLoadedRecipeId === recipeId ? null : prev.currentLoadedRecipeId // Clear if deleting current recipe
    }));
  };

  const getCurrentLoadedRecipeId = () => {
    return preferences.currentLoadedRecipeId;
  };

  const setCurrentLoadedRecipeId = (recipeId: string | null) => {
    setPreferences(prev => ({
      ...prev,
      currentLoadedRecipeId: recipeId
    }));
  };

  return (
    <PreferencesContext.Provider value={{ 
      preferences, 
      updatePreference, 
      toggleFavorite, 
      isFavorite,
      addCustomFood,
      editCustomFood,
      deleteCustomFood,
      getCustomFoods,
      getCustomFood,
      // Recipe functions
      addIngredientToRecipe,
      removeIngredientFromRecipe,
      clearCurrentRecipe,
      getCurrentRecipeIngredients,
      saveRecipe,
      updateRecipe,
      getRecipes,
      deleteRecipe,
      getCurrentLoadedRecipeId,
      setCurrentLoadedRecipeId
    }}>
      {children}
    </PreferencesContext.Provider>
  );
};